import { useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { markFullStoryEvent, getFullStoryReplayUrl } from '@core/analytics/fullstory';
import { useBooking } from '../state';
import { isPaymentGuaranteeType } from '../state/types';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useAuth } from '@core/auth/AuthProvider';
import { redirectToAuth } from '@core/auth/authRedirect';
import { getActiveSteps, resolveNextStep, resolveNextStepAfter, getStepIdFromPath } from './steps';
import { filterTerms } from '../utils/filterTerms';
import { canBypassGuarantee } from '../utils/agentUtils';
import { shouldBypassGuarantee } from '../utils/guaranteeBypass';
import { applySelectedMenuFilter } from '../utils/menuFilter';
import { getActivePairings, menuOffersPairings, selectedWlMenu } from '../utils/pairings';
import { resolveActiveSlotMenu, scopeSlotMenus } from '../utils/activeSlotMenu';
import { calculatePairingsFee, pairingsPricedInUsd } from '../utils/calculateFee';
import { computeSlotFee } from '../utils/slotFee';
import { PUBLIC_SEARCH_MODE } from '@shared/types/restaurant';
import type { BookingStepId, FlowContext, BookingFlowResult, StepNavigateOptions } from './types';
import type { BookingContextValue } from '../state/types';
import { menuHasPrice } from '@shared/utils/menuPrice';

/**
 * Build the FlowContext from current booking state + restaurant config.
 *
 * This is the single place where we gather all the data the step conditions
 * need to evaluate. As more API data becomes available (areas from slot,
 * personalization flags, etc.), add it here.
 */
export function buildFlowContext(
  booking: ReturnType<typeof useBooking>,
  restaurant: ReturnType<typeof useRestaurant>,
  isAuthenticated: boolean,
  name: string | undefined,
  phone: string | undefined,
  roles: string[] | undefined,
  userRestaurantId: string | number | number[] | null | undefined,
): FlowContext {
  // Determine menus from selected time slot
  const selectedSlot =
    booking.selectedTime && booking.timeSlots
      ? booking.timeSlots.find((s) => s.hour === booking.selectedTime)
      : null;

  const rawSlotMenus = selectedSlot?.menus ?? [];
  const selectedSlotType = selectedSlot?.type ?? null;
  const rawSlotAreas = selectedSlot?.availableAreas ?? [];
  const publicSearchMode = restaurant.publicSearchMode ?? PUBLIC_SEARCH_MODE.MENU;
  const publicAreas = restaurant.publicAreas ?? [];
  // 'menu_area' is declared on the backend but not implemented end-to-end; treat
  // it as 'menu' until the combined flow is specified (see booking-flows plan).
  const isAreaFlow = publicSearchMode === PUBLIC_SEARCH_MODE.AREA;
  // Flow D: intersect slot areas with areaFilterIds so step predicates agree
  // with routing logic and SelectAreaPage (which also filter by areaFilterIds).
  const areaFilterIds = booking.areaFilterIds ?? null;
  const selectedSlotAreas = areaFilterIds && areaFilterIds.length > 0
    ? rawSlotAreas.filter((a) => areaFilterIds.includes(a.id))
    : rawSlotAreas;
  const bookableAreas = selectedSlotAreas.filter((a) => a.available);
  const areaAutoAssigned = isAreaFlow && bookableAreas.length === 1;
  const hasAvailableAreas =
    isAreaFlow && !!selectedSlot && selectedSlotAreas.length > 0;

  // In area-mode, the EXPERIENCE step operates on menus offered in the selected
  // area — not the full slot. Filter here so step conditions (menu count,
  // singleMenuHas*, etc.) reflect what the user will actually see.
  // ...and narrow by the guest's committed menu, so this engine agrees with
  // handleSelectTime and SelectAreaPage about menuCount. Without it the engine
  // reports EXPERIENCE as active while the handlers route past it, which drifts
  // breadcrumbs and goToNext. See applySelectedMenuFilter for why an empty
  // intersection falls back instead of emptying.
  const { areaScoped: areaScopedSlotMenus, menus: selectedSlotMenus } =
    scopeSlotMenus(rawSlotMenus, isAreaFlow, booking.selectedArea, booking.selectedMenuFilter);
  // The MISMATCH is computed over the ENABLED subset, because that is what the
  // three handlers narrow to before calling the same predicate. Counting a
  // disabled menu as a match here would make the engine disagree with them —
  // handler navigates to /select-experience, engine leaves the step out of
  // activeSteps, Continue no-ops. `selectedSlotMenus` itself keeps disabled
  // menus on purpose (SelectExperiencePage renders them with alternative
  // times), so only the flag uses the narrower set.
  const { filterMismatched: menuFilterMismatched } = applySelectedMenuFilter(
    areaScopedSlotMenus.filter((m) => !m.disabled),
    booking.selectedMenuFilter,
  );

  // Restaurant config
  const serviceCount = (restaurant.serviceTypes ?? []).length;
  // Services with real availability for the selected date. Falls back to
  // the static serviceCount when no date has been selected yet.
  const availableServiceCount =
    (booking.availableServiceTypesForDate ?? []).length || serviceCount;
  const allowExperienceSelection = restaurant.allowExperienceSelection ?? false;
  const forceShowMenuSelection = restaurant.forceShowMenuSelection ?? false;
  // Absent (older/cached payloads, or the backend column not yet shipped)
  // MUST read as false.
  const forceShowAreaSelection = restaurant.forceShowAreaSelection ?? false;
  // Same `?? false` contract for the AREA_FILTER opt-in — and it is safe here
  // despite the filter having previously been on by default for every
  // area-mode restaurant: the backend migration backfilled
  // `allow_area_filter = 1` across every restaurant already in `area` mode,
  // so a live restaurant always sends an explicit value. `false` is therefore
  // reached only by payloads predating the backend release, where the correct
  // behaviour is the NEW default (no filter) rather than resurrecting the old
  // always-on one.
  const allowAreaFilter = restaurant.allowAreaFilter ?? false;
  const activePairings = getActivePairings(restaurant.beveragePairings ?? []);
  const hasPairings = activePairings.length > 0;

  // Count distinct menus across all available time slots
  const allSlots = booking.timeSlots ?? [];
  const menuIdSet = new Set<number>();
  for (const s of allSlots) {
    for (const m of s.menus) {
      menuIdSet.add(m.id);
    }
  }
  const menuCount = menuIdSet.size;

  // Single menu guarantee/price check (for experience step visibility)
  const singleMenuHasGuarantee = selectedSlotMenus.length === 1 && !!(
    selectedSlotMenus[0].guarantee &&
    isPaymentGuaranteeType(selectedSlotMenus[0].guarantee.type) &&
    (selectedSlotMenus[0].guarantee.fee ?? 0) > 0
  );
  const singleMenuHasPrice = selectedSlotMenus.length === 1 && menuHasPrice(selectedSlotMenus[0]);
  const singleMenuIsEvent = selectedSlotMenus.length === 1 && !!selectedSlotMenus[0].eventMenu;

  // The menu MARIDAJE gates on and MaridajePage prices against (ctx.pairingMenu).
  // A menu that allows pairings but offers no menu-level list skips the step
  // instead of showing an empty page.
  const isWlFlow = booking.flowType === 'waiting_list';
  const activeSlotMenu = resolveActiveSlotMenu(rawSlotMenus, selectedSlotMenus, booking.selectedExperience?.id);
  const pairingMenu = (isWlFlow
    ? selectedWlMenu(booking.wlSlotMenus ?? [], booking.wlMenus ?? [])
    : activeSlotMenu) ?? null;
  const selectedMenuAllowsPairings = menuOffersPairings(pairingMenu);

  // Special day flag (derived from calendar data, stored in booking state at date selection)
  const isSpecialDay = booking.isSelectedDateSpecial;

  // Agent flag: compute synchronously from auth state to avoid race conditions
  // when the user logs in mid-flow (useAgentDetection effect may not have fired yet).
  const isAgent = booking.isAgentReservation ||
    canBypassGuarantee(roles, userRestaurantId, restaurant.id);

  // Personalization: check if restaurant enables any personalization option.
  // Use FILTERED slot terms (by selected time + area) so that the step is
  // skipped when raw terms exist but none apply to the current selection.
  // WL flow has no selected slot — slot terms don't apply (legacy behavior)
  const filteredSlotTerms = isWlFlow
    ? []
    : filterTerms(
        booking.slotTerms ?? [],
        booking.selectedTime,
        booking.selectedArea ? Number(booking.selectedArea.id) : null,
      );
  const hasSlotTerms = filteredSlotTerms.length > 0;
  const hasPersonalizationOptions =
    !!restaurant.allowAllergies ||
    !!restaurant.allowKidsOption ||
    !!restaurant.allowPets ||
    !!restaurant.allowVeggieMenu ||
    !!restaurant.allowAccessibility ||
    hasSlotTerms;

  // Guarantee: derive real fee from slot/menu data, keep 1 as fallback
  // so the payment step is still shown when the slot type indicates a guarantee.
  // For WL PATH B: guarantee comes from booking.guaranteeType/feeDeposit (set by WL guarantee rules API).
  let guaranteeFee = 0;
  const isWl = booking.flowType === 'waiting_list';

  // Bypass: gift card or reschedule of a guaranteed reservation.
  // The extra `!!booking.rescheduleId` guard ensures we don't bypass when
  // rescheduleOriginalType is set without a rescheduleId (defensive — both
  // are written together in ChangeDateView, but state shape allows them
  // independently).
  const bypassesGuarantee = shouldBypassGuarantee({
    hasGiftCard: !!booking.giftCardToken,
    rescheduleOriginalType: booking.rescheduleId ? booking.rescheduleOriginalType : null,
  });

  // Menu guarantee overrides slot guarantee (legacy setGuaranteeInfo: requireMenuGuarantee
  // is checked before requireSlotGuarantee). Applied here so steps.ts can decide whether
  // to show the payment step based on the resolved type, not just the slot type.
  const gMenu = activeSlotMenu;
  const menuOverridesSlot =
    gMenu?.allowGuarantee &&
    gMenu?.guarantee &&
    isPaymentGuaranteeType(gMenu.guarantee.type);

  let effectiveGuaranteeType: string | null;
  if (bypassesGuarantee) {
    effectiveGuaranteeType = 'traditional';
  } else if (isWl && isPaymentGuaranteeType(booking.guaranteeType)) {
    effectiveGuaranteeType = booking.guaranteeType;
  } else if (menuOverridesSlot && gMenu?.guarantee) {
    effectiveGuaranteeType = gMenu.guarantee.type;
  } else {
    effectiveGuaranteeType = selectedSlotType;
  }

  if (!bypassesGuarantee && isPaymentGuaranteeType(effectiveGuaranteeType)) {
    if (isWl && booking.feeDeposit && booking.feeDeposit > 0) {
      guaranteeFee = booking.feeDeposit;
    } else if (menuOverridesSlot) {
      guaranteeFee = gMenu?.guarantee?.fee ?? gMenu?.guarantee?.feeUSD ?? 1;
    } else {
      guaranteeFee = selectedSlot?.fee ?? selectedSlot?.feeUSD ?? 1;
    }

    // Pairings flagged affectsGuarantee=true contribute to the chargeable
    // amount even when the slot/menu base fee is 0. Without this, isGuaranteeRequired
    // hides /payment (guaranteeFee 0) while calculateGuaranteeFee still resolves a
    // deposit/no_show with total > 0 (it includes pairings) — so the commit's
    // payment guard would try to redirect to GUARANTEE, a step not in activeSteps.
    // Priced through calculatePairingsFee + pairingsPricedInUsd, the same pair
    // calculateGuaranteeFee uses for usePaymentFee / PersonalizePage.
    guaranteeFee += calculatePairingsFee(
      booking.selectedPairings,
      pairingsPricedInUsd(!!restaurant.settleUsd, restaurant.currency),
    );
  }

  // A tip rate to choose keeps the Personalize step even when nothing else on
  // it applies — judged from the fee the payment step will charge.
  const offersTipChoice =
    !isWl &&
    !isAgent &&
    !!computeSlotFee(booking, restaurant)?.chargesGratuity &&
    (restaurant.specificServiceChargeOptions?.length ?? 0) > 1;

  // Special event
  const isEventFlow = !!booking.specialEvent;
  const eventDateCount = booking.specialEvent?.dates?.length ?? 0;
  const eventServiceCount = booking.specialEvent?.serviceTypes?.length ?? 0;

  return {
    flowType: booking.flowType,
    isWaitingList: booking.flowType === 'waiting_list',
    isEarlyAccessFlow: booking.isEarlyAccessFlow,
    isReschedule: !!booking.rescheduleId,
    isDateLocked: booking.deepLinkDateLocked,
    hasWlAlternatives: (booking.wlAlternatives ?? []).length > 0,
    diners: booking.diners,
    selectedDate: booking.selectedDate,
    service: booking.service,
    selectedTime: booking.selectedTime,
    selectedSlotMenus,
    menuFilterMismatched,
    selectedSlotType: effectiveGuaranteeType,
    serviceCount,
    availableServiceCount,
    allowExperienceSelection,
    forceShowMenuSelection,
    forceShowAreaSelection,
    hasPairings,
    menuCount,
    singleMenuHasGuarantee,
    singleMenuHasPrice,
    singleMenuIsEvent,
    selectedMenuAllowsPairings,
    pairingMenu,
    isSpecialDay,
    isAgent,
    publicSearchMode,
    allowAreaFilter,
    publicAreas,
    areaFilterIds,
    selectedSlotAreas,
    areaAutoAssigned,
    hasAvailableAreas,
    hasPersonalizationOptions: hasPersonalizationOptions || offersTipChoice,
    hasSlotTerms,
    guaranteeFee,
    hasCardToken: !!booking.cardToken,
    skipConfirmStep: booking.skipConfirmStep,
    isAuthenticated,
    hasPhone: !!(phone && phone.trim().length > 0),
    hasName: !!(name && name.trim().length > 0),
    meitreNetworkMenuId: booking.meitreNetworkMenuId ?? null,
    preSelectedMenuId: booking.preSelectedMenuId ?? null,
    showExperience: booking.showExperience,
    isEventFlow,
    eventDateCount,
    eventServiceCount,
  };
}

/**
 * Booking flow orchestrator hook.
 *
 * Provides centralized, data-driven navigation for the booking flow.
 * Each page calls `goToNext()` / `goToPrevious()` instead of hard-coding
 * route paths. The orchestrator evaluates which steps are active and
 * handles skipping conditional steps automatically.
 *
 * @example
 * ```tsx
 * function SelectServicePage() {
 *   const { goToNext, goToPrevious, progress } = useBookingFlow();
 *
 *   return (
 *     <>
 *       <button onClick={goToPrevious}>Back</button>
 *       <ProgressBar value={progress} />
 *       <button onClick={goToNext}>Continue</button>
 *     </>
 *   );
 * }
 * ```
 */
// Once-per-session dedupe for dead-end reporting. Without it a single stuck
// guest can emit one capture PER RENDER: MaridajePage advances from an effect
// keyed on goToNext's identity, and BookingProvider rebuilds contextValue as a
// fresh literal every render, so that identity changes every render — on a
// /maridaje dead end the effect refires continuously with no user interaction,
// distorting the sizing signal this reporting exists to produce. Module scope
// (not a ref) on purpose: remounts and multiple hook consumers on the same
// page must still count as ONE stuck guest.
const reportedDeadEnds = new Set<string>();
/** Test-only: lets specs assert per-key once-semantics from a clean slate. */
export function __resetDeadEndReportingForTests(): void {
  reportedDeadEnds.clear();
}

export function useBookingFlow(): BookingFlowResult {
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Build context from current state
  const context = useMemo(
    () => buildFlowContext(booking, restaurant, authState.isAuthenticated, authState.name, authState.phone, authState.roles, authState.userRestaurantId),
    [booking, restaurant, authState.isAuthenticated, authState.name, authState.phone, authState.roles, authState.userRestaurantId],
  );

  // Determine active steps
  const activeSteps = useMemo(() => getActiveSteps(context), [context]);

  // Find current step by matching URL path
  const currentStepIndex = useMemo(
    () => activeSteps.findIndex((s) => s.path === location.pathname),
    [activeSteps, location.pathname],
  );

  const currentStep = currentStepIndex >= 0 ? activeSteps[currentStepIndex] : null;
  const totalSteps = activeSteps.length;

  // Progress: 0% at first step, 100% at last step
  const progress = useMemo(() => {
    if (totalSteps <= 1) return 100;
    if (currentStepIndex < 0) return 0;
    return Math.round(((currentStepIndex + 1) / totalSteps) * 100);
  }, [currentStepIndex, totalSteps]);

  // Navigation helpers — read through a ref so callers invoking these after a
  // synchronous state commit (e.g. via flushSync) pick up the fresh activeSteps
  // instead of the memoized snapshot captured in their closure.
  const navStateRef = useRef({ activeSteps, currentStepIndex, totalSteps, pathname: location.pathname });
  navStateRef.current = { activeSteps, currentStepIndex, totalSteps, pathname: location.pathname };

  const getNextStep = useCallback(() => {
    const { activeSteps: steps, currentStepIndex: idx, totalSteps: total } = navStateRef.current;
    if (idx < 0 || idx >= total - 1) return null;
    return steps[idx + 1];
  }, []);

  const getPreviousStep = useCallback(() => {
    const { activeSteps: steps, currentStepIndex: idx } = navStateRef.current;
    if (idx <= 0) return null;
    return steps[idx - 1];
  }, []);

  const breadcrumbContextRef = useRef({ currentStepId: currentStep?.id, booking, restaurant, authState });
  breadcrumbContextRef.current = { currentStepId: currentStep?.id, booking, restaurant, authState };

  const emitStepBreadcrumb = useCallback(
    (direction: 'next' | 'previous' | 'jump', toStep: string, toPath: string) => {
      const { currentStepId, booking: b } = breadcrumbContextRef.current;
      Sentry.addBreadcrumb({
        category: 'booking-flow',
        message: `${direction} -> ${toStep}`,
        data: {
          direction,
          fromStep: currentStepId,
          toStep,
          toPath,
          diners: b.diners,
          date: b.selectedDate,
          service: b.service,
          flowType: b.flowType,
        },
        level: 'info',
      });
    },
    [],
  );

  // Shared navigation for a resolved "next" step — used by both goToNext (which
  // resolves from the render snapshot) and goToNextWith (which resolves from
  // freshly-committed values). Keeps the SIGN_IN redirect and the
  // BOOKING_FINAL history-replace policy in one place.
  // The dead-end class made measurable: standing on a canonical booking step
  // that is NOT in activeSteps (the step invalidated itself, or a stale deep
  // link) and resolving no next step. Navigation stays a strict no-op — but
  // the event must reach Sentry with enough context to reproduce and size a
  // specific instance, matching the house pattern (BookingFinalPage guard):
  // tags for grouping/filtering, a PII-safe diagnostic snapshot, an explicit
  // fingerprint, and the FullStory replay. See resolveNextStepAfter's
  // docblock (steps.ts) for the class and the fix pattern.
  const reportDeadEnd = useCallback((deadEnd: { pathname: string; stepId: string }) => {
    const dedupeKey = `${deadEnd.pathname}|${deadEnd.stepId}`;
    if (reportedDeadEnds.has(dedupeKey)) return;
    reportedDeadEnds.add(dedupeKey);
    const { booking: b, restaurant: r } = breadcrumbContextRef.current;
    markFullStoryEvent('booking_flow_dead_end', {
      path: deadEnd.pathname,
      step: deadEnd.stepId,
      restaurant: r?.subdomainPrefix ?? '<unknown>',
      flow_type: b.flowType,
    });
    const fullstoryReplayUrl = getFullStoryReplayUrl();
    Sentry.addBreadcrumb({
      category: 'booking-flow',
      message: `dead-end: ${deadEnd.pathname} (${deadEnd.stepId}) is not an active step`,
      level: 'warning',
    });
    Sentry.captureMessage(
      `booking-flow dead-end: ${deadEnd.pathname} resolved no next step while inactive`,
      {
        level: 'warning',
        tags: {
          'restaurant.slug': r?.subdomainPrefix ?? '<unknown>',
          dead_end_step: deadEnd.stepId,
        },
        extra: {
          // PII-safe by construction: IDs, dates, booleans, enums only.
          diagnosticState: {
            diners: b.diners,
            selectedDate: b.selectedDate,
            selectedTime: b.selectedTime,
            service: b.service,
            flowType: b.flowType,
            hasCardToken: !!b.cardToken,
          },
          fullstoryReplayUrl,
        },
        fingerprint: ['booking-flow-deadend', deadEnd.stepId],
      },
    );
  }, []);

  // `deadEnd` is computed BY THE RESOLUTION SITE from the same context that
  // produced `next === null` — never re-derived here from the render snapshot.
  // goToNextWith(override)/goToNextFromStep resolve against a FRESH context
  // that can disagree with navStateRef (a handler that mutates state and
  // navigates in one synchronous pass), so a snapshot-based classifier would
  // silently miss exactly the class this exists to surface.
  const navigateToNextStep = useCallback((
    next: ReturnType<typeof getNextStep>,
    deadEnd: { pathname: string; stepId: string } | null = null,
    options?: StepNavigateOptions,
  ) => {
    if (!next) {
      if (deadEnd) reportDeadEnd(deadEnd);
      return;
    }
    if (next.id === 'SIGN_IN') {
      emitStepBreadcrumb('next', 'SIGN_IN', '/sign-in');
      const b = breadcrumbContextRef.current.booking;
      redirectToAuth('login', {
        diners: b.diners,
        selectedDate: b.selectedDate,
        service: b.service,
        selectedTime: b.selectedTime,
        flowType: b.flowType,
        specialEventSlug: b.specialEvent?.slug,
        isWaitingList: b.flowType === 'waiting_list',
      });
      return;
    }
    emitStepBreadcrumb('next', next.id, next.path);
    // Replace history on the final transition into BOOKING_FINAL so the
    // commit-host step (/payment, /personalize, /confirmation depending
    // on flag + processor) doesn't linger in the browser back stack.
    // After commit, BookingFinalPage calls resetBooking() and clears
    // sessionStorage — a back-button from /booking-final would otherwise
    // re-mount the commit host with empty state, which the route guards
    // catch and redirect to / (see Sentry MEITRE-FRONTEND-V2-BF, ~23/h
    // before this fix). Replacing the history entry removes the broken
    // state from the user's reach entirely.
    //
    // Callers can also force it via `options.replace` — recovery navigations
    // away from an inactive step must not leave that step behind as a live
    // Back target (see StepNavigateOptions).
    const navOptions =
      next.id === 'BOOKING_FINAL' || options?.replace ? { replace: true } : undefined;
    navigate(next.path, navOptions);
  }, [navigate, emitStepBreadcrumb, reportDeadEnd]);

  // Forward navigation that resolves the next step from `{ ...booking,
  // ...override }` instead of the memoized render snapshot. Pages that select
  // something and immediately advance (e.g. time, area) pass the values they're
  // committing so the next-step decision reflects them right away — this
  // removes the need to flushSync the commit before navigating, which was the
  // root cause of the MARIDAJE-skip bug. Called with no argument it resolves
  // from the current snapshot, identical to goToNext().
  const goToNextWith = useCallback((override?: Partial<BookingContextValue>) => {
    if (!override) {
      // Snapshot path: getNextStep() reads navStateRef, so the same ref IS
      // the resolution context — idx < 0 on a canonical step is a dead end.
      const next = getNextStep();
      let deadEnd: { pathname: string; stepId: string } | null = null;
      if (!next) {
        const { currentStepIndex: idx, pathname } = navStateRef.current;
        const stepId = idx < 0 ? getStepIdFromPath(pathname) : null;
        if (stepId) deadEnd = { pathname, stepId };
      }
      navigateToNextStep(next, deadEnd);
      return;
    }
    const freshContext = buildFlowContext(
      { ...booking, ...override },
      restaurant,
      authState.isAuthenticated,
      authState.name,
      authState.phone,
      authState.roles,
      authState.userRestaurantId,
    );
    const next = resolveNextStep(freshContext, location.pathname);
    let deadEnd: { pathname: string; stepId: string } | null = null;
    if (!next) {
      // Classify against the SAME fresh context that resolved null — the
      // render snapshot can still show this step as active when the override
      // itself invalidated it (mutate-and-navigate in one synchronous pass).
      const stepId = getStepIdFromPath(location.pathname);
      const stillActive = stepId
        ? getActiveSteps(freshContext).some((st) => st.path === location.pathname)
        : true;
      if (stepId && !stillActive) deadEnd = { pathname: location.pathname, stepId };
    }
    navigateToNextStep(next, deadEnd);
  }, [
    navigateToNextStep,
    getNextStep,
    booking,
    restaurant,
    authState.isAuthenticated,
    authState.name,
    authState.phone,
    authState.roles,
    authState.userRestaurantId,
    location.pathname,
  ]);

  const goToNext = useCallback(() => {
    goToNextWith();
  }, [goToNextWith]);

  // Explicit advance for a handler whose own step may no longer be active —
  // resolves from the step's CANONICAL position instead of the current path.
  // See resolveNextStepAfter's contract docblock (steps.ts) for when this is
  // safe and the WL counterexample for when it is not.
  const goToNextFromStep = useCallback((stepId: BookingStepId, options?: StepNavigateOptions) => {
    // Read through the render-assigned ref, NOT the callback closure. This is
    // the primitive advertised for handlers whose step may self-invalidate —
    // the same handlers that use the flushSync-commit-then-advance pattern.
    // After a flushSync the re-render has already refreshed the ref, while a
    // closure captured on the PREVIOUS render would resolve against
    // pre-mutation state and navigate confidently to the wrong step.
    const { booking: b, restaurant: r, authState: a } = breadcrumbContextRef.current;
    const freshContext = buildFlowContext(
      b,
      r,
      a.isAuthenticated,
      a.name,
      a.phone,
      a.roles,
      a.userRestaurantId,
    );
    const next = resolveNextStepAfter(freshContext, stepId);
    let deadEnd: { pathname: string; stepId: string } | null = null;
    if (!next) {
      // Null here is legitimate only when the from-step is itself the LAST
      // active step (nothing canonically after — e.g. BOOKING_FINAL). Any
      // other null (unknown id, or nothing active after a mid-flow step) is
      // the dead-end class resurfacing one level down: signal it.
      const active = getActiveSteps(freshContext);
      const selfIdx = active.findIndex((st) => st.id === stepId);
      const legitimateLast = selfIdx >= 0 && selfIdx === active.length - 1;
      if (!legitimateLast) {
        deadEnd = { pathname: navStateRef.current.pathname, stepId };
      }
    }
    navigateToNextStep(next, deadEnd, options);
  }, [navigateToNextStep]);

  const goToPrevious = useCallback(() => {
    const prev = getPreviousStep();
    if (prev) {
      emitStepBreadcrumb('previous', prev.id, prev.path);
      navigate(prev.path);
    } else {
      // If at first step, go to landing
      emitStepBreadcrumb('previous', 'LANDING', '/');
      navigate('/');
    }
  }, [getPreviousStep, navigate, emitStepBreadcrumb]);

  const goToStep = useCallback(
    (stepId: BookingStepId, options?: StepNavigateOptions) => {
      const step = activeSteps.find((s) => s.id === stepId);
      if (step) {
        if (step.id === 'SIGN_IN') {
          emitStepBreadcrumb('jump', 'SIGN_IN', '/sign-in');
          const b = breadcrumbContextRef.current.booking;
          redirectToAuth('login', {
            diners: b.diners,
            selectedDate: b.selectedDate,
            service: b.service,
            selectedTime: b.selectedTime,
            flowType: b.flowType,
            specialEventSlug: b.specialEvent?.slug,
            isWaitingList: b.flowType === 'waiting_list',
          });
        } else {
          emitStepBreadcrumb('jump', step.id, step.path);
          // History-replace policy on BOOKING_FINAL entry — see the matching
          // comment in goToNext above. Commit success paths (services/commit/
          // shared.ts and commitReservation.ts) jump here via goToStep, so this
          // is the call site that actually fixes Sentry MEITRE-FRONTEND-V2-BF.
          // `options.replace` is the caller-forced variant — see the matching
          // comment in navigateToNextStep.
          const navOptions =
            step.id === 'BOOKING_FINAL' || options?.replace ? { replace: true } : undefined;
          navigate(step.path, navOptions);
        }
      }
    },
    [activeSteps, navigate, emitStepBreadcrumb],
  );

  return {
    activeSteps,
    currentStep,
    currentStepIndex,
    totalSteps,
    progress,
    goToNext,
    goToNextWith,
    goToNextFromStep,
    goToPrevious,
    goToStep,
    getNextStep,
    getPreviousStep,
    isFirstStep: currentStepIndex === 0,
    isLastStep: currentStepIndex === totalSteps - 1,
    context,
  };
}
